import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { getData } from "../../../actions/apiCall";
import { Button, Drawer, Modal, Space, Spin, Typography } from "antd";
import FormSetup from "../../../components/Forms/FormSetup";
import { MasterConfigs } from "../../../interfaces/entity";
import { postData } from "../../../actions/developer";
import { FormInstance } from "antd/lib";
import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
const { Title } = Typography;

const MailsComponent = ({ Email, sectionRefs, highlighted }) => {
    const [mails, setMails] = useState([]);
    const { user } = useSelector((state: any) => state.app);
    const [selectedMail, setSelectedMail] = useState(null);
    const [sendMailVisible, setSendMailVisible] = useState(false);
    const [form, setForm] = useState<FormInstance | null>(null);
    const [loading, setLoading] = useState(false);
    const [templateOptions, setTemplateOptions] = useState([]);
    const dispatch = useDispatch();

    const getAllMails = async () => {
        try {
            setLoading(true);
            const resData = await dispatch(getData(`/mail/replies/${Email}`, {
                userId: user._id
            }, { baseURL: process.env.MAIL_SERVICE }));
            if (resData && resData.code == 200) {
                setMails(resData.data);
            }
        } catch (error) {
            console.error('Error fetching mails:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (form) {
            form.setFieldsValue({
                to: Email
            });
        }
    }, [form]);

    useEffect(() => {
        getAllMails();
    }, []);


    const sendMail = async (value) => {

        if (value.to) {
            const toList = value.to.split(',').map(item => item.trim());
            value.to = toList;
        }

        if (value.cc) {
            const ccList = value.cc.split(',').map(item => item.trim());
            value.cc = ccList;
        }

        if (value.bcc) {
            const bccList = value.bcc.split(',').map(item => item.trim());
            value.bcc = bccList;
        }


        const resData = await dispatch(postData(`/mail/send`, {
            userId: user._id,
            ...value
        }, { baseURL: process.env.MAIL_SERVICE }));

        if (resData) {
            setSendMailVisible(false);
            getAllMails();
        }
        return { code: 200, message: 'Mail sent successfully' };
    }

    const config: MasterConfigs = {
        api: () => '',
        title: 'Send Mail',
        modalTitle: (action) => 'Send Mail',
        modalProps: {
            width: '60%',
        },
        onSubmit: async (id, value) => {
            return await sendMail(value)
        },
        setForm: (formInstance) => setForm(formInstance),
        getFormColumns: () => ([
            {
                label: 'To',
                name: 'to',
                type: 'text',
                span: 12,
                rules: [{ required: true, message: 'Please enter recipient email' }],
            },
            {
                label: 'CC',
                name: 'cc',
                span: 12,
                type: 'text',
            },
            {
                label: 'BCC',
                name: 'bcc',
                span: 12,
                type: 'text',
            },
            {
                label: 'template',
                name: 'template',
                type: 'select',
                selectProps: {
                    showSearch: true,
                    onChange: (value) => {
                        console.log('Selected template:', value);
                        if (form && templateOptions.length > 0) {
                            const option: any = templateOptions.find(opt => opt._id === value);
                            form.setFieldsValue({
                                subject: option.subject,
                                body: option.description,
                                cc: option.cc || form.getFieldValue('cc') || '',
                                bcc: option.bcc || form.getFieldValue('bcc') || '',
                            });
                        }
                    },
                },
                isCombo: true,
                span: 12,
            },
            {
                label: 'Subject',
                name: 'subject',
                type: 'text',
                span: 12,
                rules: [{ required: true, message: 'Please enter email subject' }],
            },
            {
                label: 'Body',
                name: 'body',
                type: 'quill',
                span: 24,
                rules: [{ required: true, message: 'Please enter email body' }],
            },
        ]),
        combo: [
            {
                name: 'template',
                api: '/cms/emailTemplates',
                idKey: '_id',
                render: (value, data) => {
                    setTemplateOptions(data);
                    return value.name;
                }
            }
        ]
    }

    return (
        <>
            <FormSetup open={sendMailVisible} onClose={() => setSendMailVisible(false)} config={config} isModal={true} />

            <Modal title="View Mail" open={!!selectedMail} footer={null} onCancel={() => setSelectedMail(null)} width={1200}>
                {selectedMail && <div>
                    <h2 className="text-xl font-bold mb-4">{selectedMail.subject}</h2>
                    <p className="mb-2"><strong>From:</strong> {selectedMail.from.emailAddress.name} &lt;{selectedMail.from.emailAddress.address}&gt;</p>
                    <p className="mb-2"><strong>To:</strong> {selectedMail.toRecipients.map(recipient => `${recipient.emailAddress.name} <${recipient.emailAddress.address}>`).join(', ')}</p>
                    <p className="mb-4"><strong>Date:</strong> {new Date(selectedMail.sentDateTime).toLocaleString()}</p>
                    <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: selectedMail.body.content }}></div>
                </div>}
            </Modal>
            <div className="border rounded-lg mb-4 bg-white"
                ref={el => (sectionRefs.current['emails'] = el)}
            >
                <div className="flex justify-between items-center py-2 px-4 border-b">
                    <Title level={5} className={highlighted === 'emails' ? 'bg-yellow-200 px-1' : ''}>Emails</Title>
                    <Space>
                        {loading && <Spin spinning={loading} indicator={<LoadingOutlined />} />}
                        {!loading && <Button type="link" icon={<ReloadOutlined />} onClick={getAllMails} />}
                        <Button type="text" onClick={() => setSendMailVisible(true)}>Send Email</Button>
                    </Space>
                </div>
                {mails.map((mail) => (
                    <div key={mail.id} className="border-b p-4" onClick={() => setSelectedMail(mail)} style={{ cursor: 'pointer' }}>
                        <div>
                            <span className="font-semibold">{mail.from && mail.from.emailAddress && mail.from.emailAddress.name}</span>
                            <span className="text-gray-500 ml-2">{new Date(mail.sentDateTime).toLocaleString()}</span>
                        </div>
                        <h3 className="font-semibold">{mail.subject}</h3>
                        <p className="text-sm text-gray-600">{mail.bodyPreview}</p>
                    </div>
                ))}
                {(!loading && mails.length === 0) && <div className="p-4 text-center text-gray-500">No emails found.</div>}
            </div>
        </>
    );
}


export default MailsComponent;